// src/components/exam-player/renderers/ShortTextRenderer.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

function StemView({ html, isRtl }) {
  if (!html) return null;
  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="prose max-w-none text-gray-800 mb-3"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default function ShortTextRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
  hideStem = false,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const body = item?.body || {};
  const layout = body.layout || "top-bottom";
  const maxLen = body.maxLen ?? 200;

  const stemHtml = useMemo(
    () => item?.stem?.fa || item?.stem?.en || "",
    [item]
  );

  // مقدار اولیه از initialAnswers (string یا {text} یا {value})
  const initialValue = useMemo(() => {
    if (
      typeof initialAnswers === "string" ||
      typeof initialAnswers === "number"
    ) {
      return String(initialAnswers);
    }
    if (
      initialAnswers &&
      typeof initialAnswers === "object" &&
      (initialAnswers.text != null || initialAnswers.value != null)
    ) {
      return String(initialAnswers.text ?? initialAnswers.value ?? "");
    }
    return "";
  }, [initialAnswers]);

  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    // به بک‌اند فقط string می‌فرستیم
    onAnswerChange?.(value);
  }, [value, onAnswerChange]);

  const InputBox = (
    <div className="w-full">
      <input
        type="text"
        dir={isRtl ? "rtl" : "ltr"}
        className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        maxLength={maxLen || undefined}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t("short_text.answer_ph")}
      />
      <div className="mt-1 text-[11px] text-gray-500">
        {maxLen
          ? t("short_text.max_len_hint", {
              defaultValue: "حداکثر {{n}} کاراکتر",
              n: maxLen,
            })
          : null}
      </div>
    </div>
  );

  let content = null;

  if (layout === "left-right") {
    content = (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          {!hideStem && <StemView html={stemHtml} isRtl={isRtl} />}
        </div>
        <div>{InputBox}</div>
      </div>
    );
  } else if (layout === "right-left") {
    content = (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>{InputBox}</div>
        <div>
          {!hideStem && <StemView html={stemHtml} isRtl={isRtl} />}
        </div>
      </div>
    );
  } else if (layout === "bottom-top") {
    content = (
      <>
        {InputBox}
        {!hideStem && (
          <div className="mt-4">
            <StemView html={stemHtml} isRtl={isRtl} />
          </div>
        )}
      </>
    );
  } else {
    // top-bottom (پیش‌فرض)
    content = (
      <>
        {!hideStem && <StemView html={stemHtml} isRtl={isRtl} />}
        {InputBox}
      </>
    );
  }

  return (
    <div className="rounded-lg bg-white p-4 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        {!hideStem && (
          <div className="text-xs text-gray-500">
            {t("preview.question_n", "Question")} {questionNumber}
          </div>
        )}
      </div>
      {content}
    </div>
  );
}
